import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Send, Users, Link2, Mail, Loader2, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MERGE_FIELDS = ['{first_name}', '{name}', '{loved_one}', '{order_ref}', '{company}'];

export default function BulkMessages() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const isSuper = user?.role === 'super_admin';

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [onlyFamily, setOnlyFamily] = useState(false);
  const [postToFamily, setPostToFamily] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState({ count: 0, with_family_page: 0, sample: [] });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingLinks, setSendingLinks] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [jobs, setJobs] = useState([]);

  const companyParam = () => (isSuper ? { company_id: selectedCompany } : {});
  const blocked = isSuper && !selectedCompany;

  const fetchPreview = useCallback(async () => {
    if (isSuper && !selectedCompany) { setPreview({ count: 0, with_family_page: 0, sample: [] }); return; }
    setLoadingPreview(true);
    try {
      const r = await axios.post(`${API}/bulk/recipients/preview`, { active_only: activeOnly, only_with_family_page: onlyFamily, ...(isSuper ? { company_id: selectedCompany } : {}) }, auth);
      setPreview(r.data);
    } catch (e) { /* ignore */ } finally { setLoadingPreview(false); }
  }, [activeOnly, onlyFamily, selectedCompany]); // eslint-disable-line

  const fetchJobs = useCallback(async () => {
    try { const r = await axios.get(`${API}/bulk/jobs`, auth); setJobs(r.data.jobs || []); } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isSuper) return;
    axios.get(`${API}/bulk/companies`, auth).then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => { fetchPreview(); }, [fetchPreview]);
  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const insertField = (f) => setBody((b) => `${b}${f}`);

  const runJob = async (jobId, total, type) => {
    setActiveJob({ id: jobId, status: 'running', total, sent: 0, failed: 0, type });
    let finished = false;
    while (!finished) {
      try {
        const r = await axios.post(`${API}/bulk/process/${jobId}`, {}, auth);
        setActiveJob({ id: jobId, status: r.data.finished ? 'done' : 'running', total: r.data.total, sent: r.data.sent, failed: r.data.failed, type });
        finished = r.data.finished;
      } catch (e) {
        toast.error('Sending interrupted — press the button again to resume the rest');
        setActiveJob((j) => j ? { ...j, status: 'paused' } : j);
        return;
      }
    }
    fetchJobs();
    toast.success('All done!');
  };

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and message first'); return; }
    if (blocked) { toast.error('Select a company first'); return; }
    setSendingTest(true);
    try {
      const r = await axios.post(`${API}/bulk/test`, { subject, body, ...companyParam() }, auth);
      toast.success(`Test sent to ${r.data.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send test'); }
    finally { setSendingTest(false); }
  };

  const sendBulk = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and message first'); return; }
    if (blocked) { toast.error('Select a company first'); return; }
    if (!window.confirm(`Send this message to ${preview.count} customer(s)? Keep this tab open until it finishes.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/bulk/start`, {
        type: 'message', subject, body, active_only: activeOnly, only_with_family_page: onlyFamily,
        post_to_family_page: postToFamily, ...companyParam()
      }, auth);
      toast.success(`Sending to ${r.data.total} customers…`);
      await runJob(r.data.job_id, r.data.total, 'message');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); }
    finally { setSending(false); }
  };

  const sendFamilyLinks = async () => {
    if (blocked) { toast.error('Select a company first'); return; }
    if (!window.confirm('Create a Family Page for every active memorial order (if not already) and email each customer their private link now? Keep this tab open until it finishes.')) return;
    setSendingLinks(true);
    try {
      const r = await axios.post(`${API}/bulk/start`, { type: 'family_links', active_only: true, ...companyParam() }, auth);
      toast.success(`Sending links to ${r.data.total} customers…`);
      await runJob(r.data.job_id, r.data.total, 'family_links');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); }
    finally { setSendingLinks(false); }
  };

  const pct = activeJob && activeJob.total ? Math.round(((activeJob.sent + activeJob.failed) / activeJob.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="bulk-back"><ArrowLeft className="w-4 h-4 mr-1" /> Dashboard</Button>
        <h1 className="text-2xl font-bold text-slate-800 mb-1">Bulk Messages</h1>
        <p className="text-slate-500 mb-6">Email all your customers at once — service updates, closures, lead times.</p>

        {/* Company selector (super admin only) — bulk is ALWAYS scoped to one company */}
        {isSuper && (
          <Card className="mb-5 border-amber-200 bg-amber-50" data-testid="bulk-company-select-card">
            <CardContent className="py-4">
              <Label className="text-sm font-medium text-amber-900">Which company's customers?</Label>
              <p className="text-xs text-amber-800/80 mb-2">You're a platform super-admin, so you must choose a single company. Emails only ever go to the customers of the company you select, using that company's branding.</p>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full border border-amber-300 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="bulk-company-select"
              >
                <option value="">— Select a company —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </CardContent>
          </Card>
        )}

        {/* Active job progress */}
        {activeJob && (
          <Card className="mb-5 border-teal-200" data-testid="bulk-progress">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-2">
                {activeJob.status === 'done' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : activeJob.status === 'paused' ? <Clock className="w-5 h-5 text-amber-500" /> : <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />}
                <span className="font-medium text-slate-700">
                  {activeJob.status === 'done' ? 'Finished' : activeJob.status === 'paused' ? 'Paused' : (activeJob.type === 'family_links' ? 'Sending family page links…' : 'Sending messages…')}
                </span>
                <span className="ml-auto text-sm text-slate-500">{activeJob.sent + activeJob.failed} / {activeJob.total}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-xs text-slate-500 mt-2">{activeJob.sent} sent{activeJob.failed ? ` · ${activeJob.failed} failed` : ''}</div>
              {activeJob.status === 'paused' && (
                <Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700" onClick={() => runJob(activeJob.id, activeJob.total, activeJob.type)} data-testid="bulk-resume">
                  Resume sending
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick action: send everyone the family page link */}
        <Card className="mb-5 border-l-4 border-l-teal-500" data-testid="bulk-family-links-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Link2 className="w-5 h-5 text-teal-600" /> Send everyone their Family Page link</CardTitle>
            <CardDescription>Creates a private Family Page for every active memorial order (if one doesn't exist yet) and emails each customer a warm welcome with their link. Anyone who's already been sent their link is skipped automatically.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={sendFamilyLinks} disabled={sendingLinks || blocked} className="bg-teal-600 hover:bg-teal-700" data-testid="bulk-send-family-links">
              {sendingLinks ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send links to all active customers
            </Button>
          </CardContent>
        </Card>

        {/* Compose */}
        <Card data-testid="bulk-compose-card">
          <CardHeader>
            <CardTitle className="text-lg">Compose a message</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Audience */}
            <div className="rounded-lg border p-4 bg-slate-50 space-y-3">
              <div className="flex items-center gap-2 text-slate-700 font-medium"><Users className="w-4 h-4" /> Who gets this
                <span className="ml-auto text-teal-700 font-semibold" data-testid="bulk-recipient-count">
                  {loadingPreview ? '…' : `${preview.count} customer${preview.count === 1 ? '' : 's'}`}
                </span>
              </div>
              <label className="flex items-center justify-between text-sm">
                <span>Active orders only (exclude completed/archived)</span>
                <Switch checked={activeOnly} onCheckedChange={setActiveOnly} data-testid="bulk-active-only" />
              </label>
              <label className="flex items-center justify-between text-sm">
                <span>Only customers with a Family Page</span>
                <Switch checked={onlyFamily} onCheckedChange={setOnlyFamily} data-testid="bulk-only-family" />
              </label>
              <p className="text-xs text-slate-400">Duplicate email addresses and anyone marked “do not contact” are automatically excluded.</p>
            </div>

            <div>
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. An update from {company}" data-testid="bulk-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Write your message to customers…" data-testid="bulk-body" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-xs text-slate-400 self-center">Insert:</span>
                {MERGE_FIELDS.map((f) => (
                  <button key={f} onClick={() => insertField(f)} className="text-xs bg-slate-100 hover:bg-slate-200 rounded px-2 py-1 text-slate-600" data-testid={`bulk-merge-${f.replace(/[{}]/g, '')}`}>{f}</button>
                ))}
              </div>
            </div>

            <label className="flex items-center justify-between text-sm rounded-lg border p-3">
              <span>Also post this as an update on each Family Page (for customers who have one)</span>
              <Switch checked={postToFamily} onCheckedChange={setPostToFamily} data-testid="bulk-post-family" />
            </label>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" onClick={sendTest} disabled={sendingTest || blocked} data-testid="bulk-send-test">
                {sendingTest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Send test to me
              </Button>
              <Button onClick={sendBulk} disabled={sending || blocked || preview.count === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="bulk-send">
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send to {preview.count} customer{preview.count === 1 ? '' : 's'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* History */}
        {jobs.length > 0 && (
          <Card className="mt-5" data-testid="bulk-history">
            <CardHeader><CardTitle className="text-lg">Recent sends</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {jobs.map((j) => (
                  <div key={j.id} className="flex items-center gap-3 text-sm border-b pb-2 last:border-0">
                    {j.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-medium text-slate-700 truncate">{j.type === 'family_links' ? 'Family Page links' : j.subject}</div>
                      <div className="text-xs text-slate-400">{new Date(j.created_at).toLocaleString()} · by {j.created_by}</div>
                    </div>
                    <div className="ml-auto text-xs text-slate-500 text-right shrink-0">{j.sent}/{j.total} sent{j.failed ? ` · ${j.failed} failed` : ''}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
